import { type FC, useEffect, useRef, useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import alpharunLogoUrl from "../../assets/researchAgent/alpharunLogo.png";
import { useMastraChatContext } from "../agents/mastra/mastraChatContext.js";
import { useResearchAgentProps } from "./researchAgentContext.js";

/**
 * Top-bar trigger for the research agent. Toggles whichever chat surface the
 * viewport supports and surfaces a ready indicator when a run completes while
 * the chat is closed.
 */
export const AskAlpharunButton: FC = () => {
  const researchAgent = useResearchAgentProps();
  const { isRunning } = useMastraChatContext();
  const isChatVisible = researchAgent?.isChatVisible ?? false;

  const [hasUnreadUpdate, setHasUnreadUpdate] = useState(false);
  const didRunWhileClosedRef = useRef(false);

  useEffect(() => {
    if (isChatVisible) {
      didRunWhileClosedRef.current = false;
      setHasUnreadUpdate(false);
      return;
    }

    if (isRunning) {
      didRunWhileClosedRef.current = true;
      setHasUnreadUpdate(false);
      return;
    }

    if (didRunWhileClosedRef.current) {
      didRunWhileClosedRef.current = false;
      setHasUnreadUpdate(true);
    }
  }, [isChatVisible, isRunning]);

  if (!researchAgent) {
    return null;
  }

  const { onOpenChat, onCloseChat } = researchAgent;
  const showsReadyState = hasUnreadUpdate && !isChatVisible && !isRunning;

  const handlePress = () => {
    if (isChatVisible) {
      onCloseChat();
    } else {
      onOpenChat();
    }
  };

  return (
    <AriaButton
      aria-label={isChatVisible ? "Hide chat" : "Show chat"}
      className="inline-flex h-8 items-center gap-2 rounded-lg px-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      onPress={handlePress}
    >
      <span className="relative flex size-5 shrink-0 items-center justify-center">
        {showsReadyState ? (
          <span
            aria-hidden
            className="absolute inset-0 rounded-md bg-primary/35 motion-safe:animate-ping"
          />
        ) : null}
        <span
          className={twMerge(
            "relative z-10 flex size-5 items-center justify-center overflow-hidden rounded-md transition-colors",
            showsReadyState ? "bg-primary shadow-sm" : "bg-foreground",
            isRunning ? "p-0" : "p-1",
          )}
        >
          {isRunning ? (
            <svg
              aria-hidden
              viewBox="0 0 20 20"
              className="size-4 animate-spin"
            >
              <defs>
                <linearGradient
                  id="ask-alpharun-spinner-gradient"
                  x1="2"
                  y1="10"
                  x2="18"
                  y2="10"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor="white" />
                  <stop offset="45%" stopColor="white" stopOpacity="0.55" />
                  <stop offset="100%" stopColor="white" stopOpacity="0" />
                </linearGradient>
              </defs>
              <circle
                cx="10"
                cy="10"
                r="7"
                fill="none"
                stroke="white"
                strokeOpacity="0.2"
                strokeWidth="2"
              />
              <circle
                cx="10"
                cy="10"
                r="7"
                fill="none"
                stroke="url(#ask-alpharun-spinner-gradient)"
                strokeLinecap="round"
                strokeWidth="2"
                strokeDasharray="32 44"
              />
            </svg>
          ) : (
            <img src={alpharunLogoUrl} alt="" className="block size-full" />
          )}
        </span>
      </span>
      <span>Ask Alpharun</span>
    </AriaButton>
  );
};
